// Import enumerations.
import {
	AuthenticationType,
} from '@app/enumerations';
// Import app models
import {
	ThirdPartyAuth,
} from '@app/models/app';
// Import interfaces
import {
	IAuthentication,
} from '@app/interfaces';
// Import interface parameters
import {
	GetAccessTokenParameters,
	InitiateParameters,
	RefreshAccessTokenParameters,
	SearchAndLoadLoginSessionParameters,
	LogoutParameters,
} from '@app/interfaces/parameters';
// Import msal browser modules.
import {
	AccountInfo,
} from '@azure/msal-browser';
// Import services.
import MsalAuthenticationService from './MsalAuthenticationService';
import ThirdPartyAuthenticationService from './ThirdPartyAuthenticationService';

export default class AuthenticationService implements IAuthentication{

	private static instance: AuthenticationService;
	private authenticationServices: Map<AuthenticationType, IAuthentication>;

	private constructor() {
		this.authenticationServices = new Map<AuthenticationType, IAuthentication>([
			[AuthenticationType.MSAL, new MsalAuthenticationService()],
			[AuthenticationType.THIRD_PARTY, new ThirdPartyAuthenticationService()]
		]);
	}

	/**
	 * Get a singleton instance of AuthenticationService.
	 */
	public static getInstance(): AuthenticationService {
		if (!AuthenticationService.instance) {
			AuthenticationService.instance = new AuthenticationService();
		}
		return AuthenticationService.instance;
	}

	//#region interface methods

	/**
	 * Fetches the access token with the possible redirect.
	 * @param params
	 */
	public getAccessTokenWithRedirect(params: GetAccessTokenParameters): void {
		this.getCurrentAuthenticationService()?.getAccessTokenWithRedirect(params);
	}

	/**
	 * Force refreshes the lifetime of the token.
	 * @param params
	 */
	public refreshTokenWithRedirect(params: RefreshAccessTokenParameters): void {
		this.getCurrentAuthenticationService()?.refreshTokenWithRedirect(params);
	}

	/**
	 * Searches and loads any existing login session.
	 * @param params
	 */
	public searchAndLoadExistingLogin(params: SearchAndLoadLoginSessionParameters): void {
		this.getCurrentAuthenticationService()?.searchAndLoadExistingLogin(params);
	}

	/**
	 * Invokes a login redirect call. 
	 **/
	public login(): void {
		this.getCurrentAuthenticationService()?.login();
	}

	/**
	 * Initiates the login redirect process, if there is any.
	 * @param params
	 */
	public initiate(params: InitiateParameters): void {
		this.getCurrentAuthenticationService()?.initiate(params);
	}

	/**
	 * Logs the current authenticated user out of the application with parameters.
	 * @param params
	 */
	public logoutWithParameters(params: LogoutParameters): void {
		this.endImpersonate();
		this.getCurrentAuthenticationService()?.logoutWithParameters(params);
	}

	/**
	 * Logs the current authenticated user out of the application.
	 */
	public logout(redirectUrl: string): void {
		this.endImpersonate();
		this.getCurrentAuthenticationService()?.logout(redirectUrl);
	}

	/**
	 *  Get the current authenticated user.
	 */
	public getUser(): AccountInfo | null {
		return this.getCurrentAuthenticationService()?.getUser() ?? null;
	}

	/**
	 * Checks to see if the user is logged in. 
	 */
	public isLoggedIn(): boolean {
		return this.getCurrentAuthenticationService()?.isLoggedIn() ?? false;
	}

	//#endregion interface methods

	//#region public methods

	/**
	 * Starts impersonating another user in API requests by storing that user ID in local storage.
	 * @param userId - The userId of the user to be impersonated.
	 */
	public setImpersonate(userId: number): void {
		localStorage.setItem('impersonate_userId', userId.toString());
	}

	/**
	 * Stops impersonating other users by clearing the impersonation values in local storage.
	 */
	public endImpersonate(): void {
		localStorage.removeItem('impersonate_userId');
	}

	/**
	 * Gets the user ID of the user being impersonated, or null if no user is being impersonated.
	 */
	public getImpersonateUserId(): number | null {
		const impersonateUserId: string | null = localStorage.getItem('impersonate_userId');
		return impersonateUserId !== null ? parseInt(impersonateUserId, 10) : null;
	}

	/**
	 * Removes the current third party token.
	 */
	public removeThirdPartyAuth(): void {
		localStorage.removeItem('thirdPartyAuth');
	}

	/**
	 * Sets the current third party token.
	 * @param thirdPartyAuth
	 */
	public setThirdPartyAuth(thirdPartyAuth: ThirdPartyAuth): void {
		localStorage.setItem('thirdPartyAuth', JSON.stringify(thirdPartyAuth));
	}

	/**
	 * Determined if the ID token has expired. If it has then we need
	 * to reauthenticate the user.
	 */
	public isSessionExpired(): boolean {
		return this.getTokenLifetimeInMilliseconds() <= 0;
	}

	/**
	 * This method will get you the portion of days remaining in the token lifetime.
	 */
	public getRemainingTokenLifetimeInDays(): number {
		let tokenLifetimeInMilliseconds: number = this.getTokenLifetimeInMilliseconds();
		return Math.floor((tokenLifetimeInMilliseconds % (1000 * 60 * 60 * 60 * 24)) / (1000 * 60 * 60 * 24));
	}

	/**
	 * This method will get you the portion of hours remaining in the token lifetime.
	 */
	public getRemainingTokenLifetimeInHours(): number {
		let tokenLifetimeInMilliseconds: number = this.getTokenLifetimeInMilliseconds();
		return Math.floor((tokenLifetimeInMilliseconds % (1000 * 60 * 60 * 60)) / (1000 * 60 * 60));
	}

	/**
	 * This method will get you the portion of minutes remaining in the token lifetime.
	 */
	public getRemainingTokenLifetimeInMinutes(): number {
		let tokenLifetimeInMilliseconds: number = this.getTokenLifetimeInMilliseconds();
		return Math.floor((tokenLifetimeInMilliseconds % (1000 * 60 * 60)) / (1000 * 60));
	}

	/**
	 * This method will get you the portion of seconds remaining in the token lifetime.
	 */
	public getRemainingTokenLifetimeInSeconds(): number {
		let tokenLifetimeInMilliseconds: number = this.getTokenLifetimeInMilliseconds();
		return Math.floor((tokenLifetimeInMilliseconds % (1000 * 60)) / 1000);
	}

	/**
	 * Gets the total remaining milliseconds for the ID token lifetime.
	 * If it reaches zero, it means the ID token has expired and the user
	 * need to reauthenticate to keep using the website.
	 */
	public getTokenLifetimeInMilliseconds(): number {
		let timeLeftInMilliseconds: number = 0;

		//Make sure the user is logged in.
		if (this.isLoggedIn()) {
			const loggedInUser: AccountInfo | null = this.getUser();
			//The "exp" property in the ID token is expressed in seconds 
			//so we multiply by 1000 to convert to milliseconds.
			if (loggedInUser !== null
				&& loggedInUser.idTokenClaims !== undefined
				&& loggedInUser.idTokenClaims.exp !== undefined) {

				const expirationTimeInMilliseconds = loggedInUser.idTokenClaims.exp * 1000;
				timeLeftInMilliseconds = expirationTimeInMilliseconds - Date.now();
			}
		}

		//If the time left becomes less than zero then
		//make sure to return zero to signify the token has expired.
		if (timeLeftInMilliseconds < 0) {
			timeLeftInMilliseconds = 0;
		}

		return timeLeftInMilliseconds;
	}

	/**
	 * Gets the total remaining seconds for the ID token lifetime.
	 * If it reaches zero, it means the ID token has expired and the user
	 * need to reauthenticate to keep using the website.
	 */
	public getTokenLifetimeInSeconds(): number {
		let tokenLifetimeInMilliseconds: number = this.getTokenLifetimeInMilliseconds();
		return tokenLifetimeInMilliseconds / 1000;
	}

	//#endregion public methods

	//#region helper methods

	private getCurrentAuthenticationService(): IAuthentication | undefined {
		let isLoggedInThirdParty: boolean = this.authenticationServices.get(AuthenticationType.THIRD_PARTY)?.isLoggedIn() ?? false;
		return isLoggedInThirdParty ? this.authenticationServices.get(AuthenticationType.THIRD_PARTY) : this.authenticationServices.get(AuthenticationType.MSAL);
	}

	//#endregion helper methods
}
